import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import {
  compose,
  displayUserName,
  formatNumbers,
} from '../../../services/utilityFunctions';
import UserProp from '../../../commonComponents/propTypes/userProp';
import Notifications from '../../../services/notifications';
import CloseIcon from '../../../commonComponents/icons/icon/CloseIcon';
import Loader from '../../../services/loader';

const fetchVACVInfos = async (userId, startDate, endDate) => {
  const url = `/api/user/direct-selling/${userId}/info`;
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const { data: response } = await axios.get(url, {
    params,
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Organization',
    },
  });

  return response.data;
};

const fetchVACVInfosBinary = async (userId, datePicker, startDate, endDate) => {
  const url = `/api/user/direct-selling/binary/member/${userId}/info/${
    datePicker || 'custom'
  }`;
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const { data: response } = await axios.get(url, {
    params,
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Organization',
    },
  });

  return response.data;
};

function TeamStructureLevelSection({
  t,
  user,
  directSellingSettings,
  isSponsorTree,
  wrapperValuesClass,
  filters,
  onUnselectUser,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const loader = useRef(new Loader('#information-block'));

  useEffect(() => {
    if (isLoading) {
      loader.current.showProgressLoader('');
    } else {
      loader.current.hide();
    }
  }, [isLoading, loader]);

  const { VAIntegration, CVIntegration, salesIntegration, binaryIntegration } =
    directSellingSettings;
  const {
    skillLevel,
    level,
    personalVA,
    personalCV,
    forecast,
    personalCommission,
    groupVA: userGroupVA,
    groupCV: userGroupCV,
    groupCommissions: userGroupCommission,
  } = user;

  const [VACVInfos, setVACVInfos] = useState({
    personalVA: isSponsorTree ? null : personalVA,
    myVA: isSponsorTree ? null : null,
    groupVA: isSponsorTree ? null : userGroupVA,
    personalCV: isSponsorTree ? null : personalCV,
    myCV: isSponsorTree ? null : null,
    groupCV: isSponsorTree ? null : userGroupCV,
  });

  const { skillLevel: skillForecast } = forecast || {};
  const { name: nameForecast } = skillForecast || {};
  const currentLevel =
    skillLevel || level
      ? skillLevel?.name || level
      : t('connect.teams.structure.no_level');
  const forecastLevel = nameForecast || t('connect.teams.structure.no_level');

  const userVA = VACVInfos?.personalVA
    ? formatNumbers(VACVInfos?.personalVA, false, true)
    : 0;
  const userCV = VACVInfos?.personalCV
    ? formatNumbers(VACVInfos?.personalCV, false, true)
    : 0;
  const myVA = VACVInfos?.myVA
    ? formatNumbers(VACVInfos?.myVA, false, true)
    : 0;
  const myCV = VACVInfos?.myCV
    ? formatNumbers(VACVInfos?.myCV, false, true)
    : 0;
  const VALeft = VACVInfos?.VALeft
    ? formatNumbers(VACVInfos?.VALeft, false, true)
    : 0;
  const VARight = VACVInfos?.VARight
    ? formatNumbers(VACVInfos?.VARight, false, true)
    : 0;
  const CVLeft = VACVInfos?.CVLeft
    ? formatNumbers(VACVInfos?.CVLeft, false, true)
    : 0;
  const CVRight = VACVInfos?.CVRight
    ? formatNumbers(VACVInfos?.CVRight, false, true)
    : 0;
  const defaultUserCommission =
    personalCommission >= 0
      ? formatNumbers(personalCommission, false, true)
      : 0;
  const userCommission = VACVInfos?.personalCommission
    ? formatNumbers(VACVInfos?.personalCommission, false, true)
    : defaultUserCommission || 0;
  const groupVA = VACVInfos?.groupVA
    ? formatNumbers(VACVInfos?.groupVA, false, true)
    : 0;
  const groupCV = VACVInfos?.groupCV
    ? formatNumbers(VACVInfos?.groupCV, false, true)
    : 0;
  const defaultGroupCommission =
    userGroupCommission >= 0
      ? formatNumbers(userGroupCommission, false, true)
      : 0;
  const groupCommission = VACVInfos?.groupCommission
    ? formatNumbers(VACVInfos?.groupCommission, false, true)
    : defaultGroupCommission || 0;

  useEffect(() => {
    setIsLoading(true);
    if (isSponsorTree) {
      fetchVACVInfos(user._id, filters?.startDate, filters?.endDate)
        .then(data => {
          setVACVInfos(data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      fetchVACVInfosBinary(
        user._id,
        filters?.rangeType,
        filters?.startDate,
        filters?.endDate,
      )
        .then(data => {
          setVACVInfos(data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [user, filters]);

  const hasDoubleIntegration = VAIntegration && CVIntegration;

  const renderSponsorTreeCard = () => {
    const isBinaryNoCV = binaryIntegration && !CVIntegration;
    const isBinaryAndCV = binaryIntegration && CVIntegration;

    const topDivClassName = isBinaryNoCV
      ? 'binary-integration-wrapper'
      : 'integration-wrapper';

    return (
      <div id="information-block">
        {salesIntegration && !VAIntegration && !CVIntegration && (
          <div className="integration-wrapper">
            <div className="item-wrapper">
              <span className="label label-first">
                {t('commission.modal.sale.commission')}
              </span>
              <span className="value">{`${userCommission}€`}</span>
            </div>
            <div className="item-wrapper">
              <span className="label label-second">
                {t('group.commission.abbreviation')}
              </span>
              <span className="value">{`${groupCommission}€`}</span>
            </div>
          </div>
        )}
        {VAIntegration && (
          <div className={topDivClassName}>
            {isBinaryNoCV && (
              <div className="binary-topValue">
                <span className="label label-first">
                  {t('commissions.myVA')}
                </span>
                <span className="value">{myVA}</span>
              </div>
            )}
            <div className="va-wrapper">
              {isBinaryAndCV && (
                <div className="item-wrapper">
                  <span className="label label-first">
                    {t('commissions.myVA')}
                  </span>
                  <span className="value">{myVA}</span>
                </div>
              )}
              <div className="item-wrapper">
                <span className="label label-first">
                  {t('commissions.personalVA')}
                </span>
                <span className="value">{userVA}</span>
              </div>
              <div className="item-wrapper">
                <span className="label label-second">
                  {t('commissions.groupVA')}
                </span>
                <span className="value">{groupVA}</span>
              </div>
            </div>
          </div>
        )}
        {CVIntegration && (
          <div className="integration-wrapper">
            <div className="va-wrapper">
              {binaryIntegration && (
                <div className="item-wrapper">
                  <span className="label label-first">
                    {t('commissions.myCV')}
                  </span>
                  <span className="value">{myCV}</span>
                </div>
              )}
              <div className="item-wrapper">
                <span className="label label-first">
                  {t('commissions.personalCV')}
                </span>
                <span className="value">{userCV}</span>
              </div>
              <div className="item-wrapper">
                <span className="label label-second">
                  {t('commissions.groupCV')}
                </span>
                <span className="value">{groupCV}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBinaryTreeCard = () => (
    <div id="information-block">
      <div className="integration-wrapper">
        {!CVIntegration && (
          <div className="item-wrapper binary-topValue">
            <span className="label label-first">{t('commissions.myVA')}</span>
            <span className="value">{myVA}</span>
          </div>
        )}
        <div
          className={`${
            hasDoubleIntegration ? 'integration-wrapper' : 'binary-sideValues'
          }`}
        >
          {CVIntegration && (
            <div className="item-wrapper">
              <span className="label label-first">{t('commissions.myVA')}</span>
              <span className="value">{myVA}</span>
            </div>
          )}
          <div className="item-wrapper">
            <span className="label label-first">{t('commissions.vaLeft')}</span>
            <span className="value">{VALeft}</span>
          </div>
          <div className="item-wrapper">
            <span className="label label-second">
              {t('commissions.vaRight')}
            </span>
            <span className="value">{VARight}</span>
          </div>
        </div>
      </div>
      {CVIntegration && (
        <div className="integration-wrapper">
          <div className="va-wrapper">
            {binaryIntegration && (
              <div className="item-wrapper">
                <span className="label label-first">
                  {t('commissions.myCV')}
                </span>
                <span className="value">{myCV}</span>
              </div>
            )}
            <div className="item-wrapper">
              <span className="label label-first">
                {t('commissions.cvLeft')}
              </span>
              <span className="value">{CVLeft}</span>
            </div>
            <div className="item-wrapper">
              <span className="label label-second">
                {t('commissions.cvRight')}
              </span>
              <span className="value">{CVRight}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="card-data-wrapper">
      <div className="triangle" />
      <div className={`card-data ${isSponsorTree ? '' : 'card-data-binary'}`}>
        <button className="close-button" type="button" onClick={onUnselectUser}>
          <CloseIcon />
        </button>
        <div>
          <div className="level-wrapper">
            <span className="user-text">{displayUserName(user)}</span>
            <div>
              <div className="level-container">
                <span className="label">{`${t('level')}:`}</span>
                <span className="value">{currentLevel}</span>
              </div>
              <div className="level-container">
                <span className="label">
                  {`${t('profile.sidePanel.level.forecastLevel')}:`}
                </span>
                <span className="value">{forecastLevel}</span>
              </div>
            </div>
          </div>
        </div>
        {isSponsorTree ? (
          <div
            className={`integration-container ${
              hasDoubleIntegration ? 'grid-integration' : ''
            }`}
          >
            {renderSponsorTreeCard()}
          </div>
        ) : (
          <div
            className={`integration-container ${
              hasDoubleIntegration ? 'grid-integration' : ''
            } ${wrapperValuesClass}`}
          >
            {renderBinaryTreeCard()}
          </div>
        )}
      </div>
    </div>
  );
}

TeamStructureLevelSection.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  directSellingSettings: PropTypes.shape({
    VAIntegration: PropTypes.bool.isRequired,
    CVIntegration: PropTypes.bool.isRequired,
    salesIntegration: PropTypes.bool.isRequired,
    binaryIntegration: PropTypes.bool.isRequired,
  }).isRequired,
  isSponsorTree: PropTypes.bool,
  wrapperValuesClass: PropTypes.string,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    rangeType: PropTypes.string,
  }),
  onUnselectUser: PropTypes.func.isRequired,
};

TeamStructureLevelSection.defaultProps = {
  isSponsorTree: true,
  wrapperValuesClass: '',
  filters: {},
};

export default compose(translate)(TeamStructureLevelSection);
